import { Component, OnInit } from '@angular/core';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { Features } from '@app/core/user-permissions/user-permissions.model';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { DirectSubscriptionService } from '@app/modules/direct/services/direct-subscription.service';
import { BillingFacade } from '@app/shared/+state/billing';
import { UsersFacade } from '@app/shared/+state/users';
import { DeviceDetectionService } from '@app/shared/services/device-detection/device-detection.service';
import { OptimizelyService } from '@app/shared/services/optimizely/optimizely.service';
import { combineLatest, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

// Re-added: Minimal interface for window.webkit.messageHandlers
interface CustomWindow extends Window {
  webkit?: {
    messageHandlers?: {
      hospitableBiometricsHandler?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        postMessage: (message: any) => void;
      };
      // Other handlers can be added here if needed
    };
  };
}

@Component({
  standalone: false,
  selector: 'sbnb-settings-subnav',
  templateUrl: './settings-subnav.component.html',
  styleUrls: ['./settings-subnav.component.scss'],
})
export class SettingsSubnavComponent implements OnInit {
  Features = Features;

  perms$ = this.userPermissionsService;

  shouldShowPayouts$ = this.authService.user$.pipe(map((user) => user?.payouts_feature?.can_access_payouts));

  enableTaxes$ = this.directSubscription.isBasic$;
  notExpired$ = this.authService.isLoggedIn$.pipe(
    switchMap((isLoggedIn) => {
      if (!isLoggedIn) {
        return of(false);
      }
      return this.billingFacade.notExpiredCheck$;
    }),
    shareReplay(1)
  );

  isIos = this.deviceDetectionService.isIosApp();
  hospitableBiometricsSetting: '0' | '1' | null = null;

  /* feature flags */
  isOnLegacyPlan$ = this.usersFacade.isOnLegacyPlan$;
  marketplaceEnabled$ = combineLatest([this.isOnLegacyPlan$]).pipe(map(([isOnLegacyPlan]) => isOnLegacyPlan === false));

  constructor(
    public authService: AuthenticationService,
    private readonly usersFacade: UsersFacade,
    public optimizely: OptimizelyService,
    private readonly userPermissionsService: UserPermissionsService,
    private directSubscription: DirectSubscriptionService,
    private readonly billingFacade: BillingFacade,
    private deviceDetectionService: DeviceDetectionService
  ) {}

  ngOnInit(): void {
    if (this.isIos) {
      try {
        const storedValue = localStorage.getItem('hospitable_biometrics_enabled');
        if (storedValue === '0' || storedValue === '1') {
          this.hospitableBiometricsSetting = storedValue;
        } else {
          this.hospitableBiometricsSetting = null;
        }
      } catch (e) {
        console.error('Failed to access localStorage for biometrics setting:', e);
        this.hospitableBiometricsSetting = null;
      }
    }
  }

  toggleFaceID(): void {
    const win = window as CustomWindow; // Use CustomWindow type
    if (this.isIos && win.webkit?.messageHandlers?.hospitableBiometricsHandler) {
      if (this.hospitableBiometricsSetting === '0' || this.hospitableBiometricsSetting === '1') {
        const action = this.hospitableBiometricsSetting === '1' ? 'disable' : 'enable';
        try {
          // Use window.webkit.messageHandlers for WKScriptMessage
          win.webkit.messageHandlers.hospitableBiometricsHandler.postMessage({ action: action });
          // The native app will handle the update to localStorage and navigate back as per the spec.
        } catch (e) {
          console.error('Error posting message to hospitableBiometricsHandler:', e);
        }
      } else {
        console.warn('toggleFaceID called with unexpected biometrics setting:', this.hospitableBiometricsSetting);
      }
    } else {
      console.warn('WebKit message handler hospitableBiometricsHandler not available or not on iOS.');
    }
  }
}
